
interface TrackingItem {
  ticketTypeId: string
  ticketTypeName: string
  quantity: number
  price: number
  eventName?: string
}

interface TrackingEvent {
  id: string | number
  title?: string
  name?: string
  category?: string
}

declare global {
  interface Window {
    dataLayer: Record<string, any>[]
    gtag: (...args: any[]) => void
    fbq: (...args: any[]) => void
    ttq: { track: (...args: any[]) => void; identify: (...args: any[]) => void; page: () => void } & Record<string, any>
  }
}

// Ensure dataLayer and gtag shim exist immediately so events are
// buffered before GA4/GTM scripts load. Both gtag.js and GTM
// process the dataLayer queue retroactively on initialization.
if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || []
  if (!window.gtag) {
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments)
    }
  }
}

// Send event via gtag() for GA4 loaded directly (not via GTM).
// gtag wants flat params: gtag('event', 'purchase', { transaction_id, value, items, ... })
const fireGtag = (eventName: string, params?: Record<string, any>) => {
  try {
    if (typeof window !== 'undefined') {
      window.gtag('event', eventName, params || {})
    }
  } catch {
    // Non-critical
  }
}

// Push event to dataLayer for GTM-based setups.
// GTM expects: dataLayer.push({ event: '...', ecommerce: { ... } })
const pushDataLayer = (event: string, ecommerceData?: Record<string, any>) => {
  try {
    window.dataLayer = window.dataLayer || []
    if (ecommerceData) {
      // Clear previous ecommerce object (GA4 best practice)
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({ event, ecommerce: ecommerceData })
    } else {
      window.dataLayer.push({ event })
    }
  } catch {
    // Non-critical
  }
}

/**
 * The tenant's ISO 4217 currency, set once by the storefront ThemeProvider.
 *
 * Every funnel event used to report a hardcoded 'USD'. On a GBP or EUR tenant
 * that told Meta, GA4 and TikTok that a £60 ticket was $60 — wrong ad ROAS,
 * wrong optimisation, wrong revenue reporting. Purchase already carried an
 * explicit currency; now the whole funnel matches it.
 */
let trackingCurrency = 'USD'

export const setTrackingCurrency = (currency?: string | null) => {
  if (currency && /^[A-Z]{3}$/.test(currency)) trackingCurrency = currency
}

// Fire both gtag (direct GA4) and dataLayer (GTM) in one call.
// gtag uses flat params; dataLayer nests under ecommerce.
const fireGA4Event = (eventName: string, params?: Record<string, any>) => {
  fireGtag(eventName, params)
  pushDataLayer(eventName, params)
}

// Fire Facebook Pixel event. `options.eventID` enables dedup against the
// server-side Conversions API copy of the same event.
const fireFBPixel = (eventName: string, params?: Record<string, any>, options?: { eventID?: string }) => {
  try {
    if (window.fbq) {
      if (options?.eventID) window.fbq('track', eventName, params, { eventID: options.eventID })
      else window.fbq('track', eventName, params)
    }
  } catch {
    // Non-critical
  }
}

// Fire TikTok Pixel event
const fireTTPixel = (eventName: string, params?: Record<string, any>) => {
  try {
    if (window.ttq) window.ttq.track(eventName, params)
  } catch {
    // Non-critical
  }
}

// ── First-party funnel beacons ──────────────────────────────────────────────
// Every funnel step also posts a tiny event to our own /api/track so the
// admin Funnel report works even when third-party pixels are blocked.
// Anonymous visitor id in localStorage; UTM parameters are captured
// FIRST-TOUCH (the landing that brought the visitor) and attached to every
// beacon. PURCHASE is deliberately not beaconed — the server records it.

const VID_KEY = 'pt_visitor_id'
const UTM_KEY = 'pt_first_utm'
const UTM_TTL_MS = 30 * 24 * 60 * 60 * 1000

export const getVisitorId = (): string | null => {
  if (typeof window === 'undefined') return null
  try {
    let vid = localStorage.getItem(VID_KEY)
    if (!vid) {
      vid = `v_${crypto.randomUUID()}`
      localStorage.setItem(VID_KEY, vid)
    }
    return vid
  } catch {
    return null
  }
}

export const getFirstTouchUtm = (): { utmSource?: string; utmMedium?: string; utmCampaign?: string } => {
  if (typeof window === 'undefined') return {}
  try {
    // First touch wins for 30 days; a new utm_source only takes over after
    // the stored attribution expires.
    const stored = localStorage.getItem(UTM_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as { utmSource?: string; utmMedium?: string; utmCampaign?: string; at?: number }
        if (parsed.at && Date.now() - parsed.at < UTM_TTL_MS) {
          return { utmSource: parsed.utmSource, utmMedium: parsed.utmMedium, utmCampaign: parsed.utmCampaign }
        }
      } catch { /* fall through to re-capture */ }
    }
    const params = new URLSearchParams(window.location.search)
    const source = params.get('utm_source')
    if (!source) return {}
    const captured = {
      utmSource: source,
      utmMedium: params.get('utm_medium') || undefined,
      utmCampaign: params.get('utm_campaign') || undefined,
    }
    localStorage.setItem(UTM_KEY, JSON.stringify({ ...captured, at: Date.now() }))
    return captured
  } catch {
    return {}
  }
}

const recordInternal = (
  eventType: 'PAGE_VIEW' | 'VIEW_EVENT' | 'ADD_TO_CART' | 'BEGIN_CHECKOUT' | 'ADD_PAYMENT',
  props?: { eventId?: string; value?: number },
) => {
  try {
    if (typeof window === 'undefined') return
    const vid = getVisitorId()
    if (!vid) return
    const utm = getFirstTouchUtm()
    const payload = JSON.stringify({
      t: eventType,
      vid,
      path: window.location.pathname,
      eventId: props?.eventId,
      value: props?.value,
      ...utm,
    })
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/track', new Blob([payload], { type: 'application/json' }))
    } else {
      fetch('/api/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {})
    }
  } catch {
    // Non-critical
  }
}

// Map items to GA4 ecommerce item format
const mapItems = (items: TrackingItem[]) =>
  items.map(i => ({
    item_id: String(i.ticketTypeId),
    item_name: i.ticketTypeName,
    item_category: i.eventName || 'Event',
    price: i.price,
    quantity: i.quantity,
  }))

export const trackPageView = (pageName: string, pageUrl?: string) => {
  recordInternal('PAGE_VIEW')
  try {
    const params = {
      page_title: pageName,
      page_location: pageUrl || window.location.href,
    }
    fireGtag('page_view', params)
    // Also push to dataLayer for GTM (flat format -- not an ecommerce event)
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ event: 'page_view', ...params })
    }
  } catch {
    // Non-critical
  }
}

export const trackViewItemList = (listName: string, events: TrackingEvent[]) => {
  const items = events.map((e, i) => ({
    item_id: String(e.id),
    item_name: e.title || e.name,
    item_category: e.category || 'Event',
    index: i,
  }))
  fireGA4Event('view_item_list', { item_list_name: listName, items })
  fireFBPixel('ViewContent', {
    content_type: 'product_group',
    content_ids: events.map(e => String(e.id)),
  })
  fireTTPixel('ViewContent', {
    content_type: 'product_group',
    content_id: events.map(e => String(e.id)).join(','),
  })
}

export const trackViewItem = (event: TrackingEvent & { price?: number }) => {
  recordInternal('VIEW_EVENT', { eventId: String(event.id), value: event.price || 0 })
  fireGA4Event('view_item', {
    currency: trackingCurrency,
    value: event.price || 0,
    items: [
      {
        item_id: String(event.id),
        item_name: event.title || event.name,
        item_category: event.category || 'Event',
        price: event.price || 0,
      },
    ],
  })
  fireFBPixel('ViewContent', {
    content_type: 'product',
    content_ids: [String(event.id)],
    content_name: event.title || event.name,
    value: event.price || 0,
    currency: trackingCurrency,
  })
  fireTTPixel('ViewContent', {
    content_type: 'product',
    content_id: String(event.id),
    content_name: event.title || event.name,
    value: event.price || 0,
    currency: trackingCurrency,
  })
}

export const trackSelectItem = (
  selectionType: string,
  item: { name?: string; title?: string; waveTime?: string; id?: string | number },
) => {
  fireGA4Event('select_item', {
    item_list_name: selectionType,
    items: [
      {
        item_id: String(item.id || ''),
        item_name: item.name || item.title || item.waveTime || '',
      },
    ],
  })
}

export const trackAddToCart = (items: TrackingItem[], total: number) => {
  recordInternal('ADD_TO_CART', { value: total })
  fireGA4Event('add_to_cart', {
    currency: trackingCurrency,
    value: total,
    items: mapItems(items),
  })
  fireFBPixel('AddToCart', {
    content_type: 'product',
    value: total,
    currency: trackingCurrency,
    contents: items.map(i => ({ id: i.ticketTypeId, quantity: i.quantity })),
  })
  fireTTPixel('AddToCart', {
    content_type: 'product',
    value: total,
    currency: trackingCurrency,
    content_id: items.map(i => i.ticketTypeId).join(','),
    quantity: items.reduce((s, i) => s + i.quantity, 0),
  })
}

export const trackRemoveFromCart = (items: TrackingItem[], value: number) => {
  fireGA4Event('remove_from_cart', {
    currency: trackingCurrency,
    value,
    items: mapItems(items),
  })
}

export const trackViewCart = (items: TrackingItem[], total: number) => {
  fireGA4Event('view_cart', {
    currency: trackingCurrency,
    value: total,
    items: mapItems(items),
  })
}

export const trackBeginCheckout = (total: number, items?: TrackingItem[], coupon?: string) => {
  const params: Record<string, any> = {
    currency: trackingCurrency,
    value: total,
    items: mapItems(items || []),
  }
  if (coupon) params.coupon = coupon
  recordInternal('BEGIN_CHECKOUT', { value: total })
  fireGA4Event('begin_checkout', params)
  fireFBPixel('InitiateCheckout', {
    value: total,
    currency: trackingCurrency,
    num_items: (items || []).reduce((s, i) => s + i.quantity, 0),
  })
  fireTTPixel('InitiateCheckout', {
    value: total,
    currency: trackingCurrency,
    content_type: 'product',
  })
}

export const trackAddPaymentInfo = (
  paymentType: string,
  total: number,
  items?: TrackingItem[],
) => {
  recordInternal('ADD_PAYMENT', { value: total })
  fireGA4Event('add_payment_info', {
    currency: trackingCurrency,
    value: total,
    payment_type: paymentType,
    items: mapItems(items || []),
  })
  fireFBPixel('AddPaymentInfo', { value: total, currency: trackingCurrency })
  fireTTPixel('AddPaymentInfo', { value: total, currency: trackingCurrency })
}

export const trackPurchase = (
  orderId: string | number,
  total: number,
  items?: TrackingItem[],
  extras?: { tax?: number; fees?: number; coupon?: string; currency?: string },
) => {
  const currency = extras?.currency || trackingCurrency
  const params: Record<string, any> = {
    transaction_id: String(orderId),
    currency,
    value: total,
    items: mapItems(items || []),
  }
  if (extras?.tax) params.tax = extras.tax
  if (extras?.fees) params.shipping = extras.fees
  if (extras?.coupon) params.coupon = extras.coupon
  fireGA4Event('purchase', params)

  // eventID = orderNumber: the server-side Conversions API purchase uses the
  // same id, so Meta/TikTok dedupe the browser and server copies of this event.
  fireFBPixel('Purchase', {
    currency,
    value: total,
    content_type: 'product',
    contents: (items || []).map(i => ({ id: i.ticketTypeId, quantity: i.quantity })),
    num_items: (items || []).reduce((s, i) => s + i.quantity, 0),
  }, { eventID: String(orderId) })
  fireTTPixel('CompletePayment', {
    currency,
    value: total,
    content_type: 'product',
    content_id: (items || []).map(i => i.ticketTypeId).join(','),
    quantity: (items || []).reduce((s, i) => s + i.quantity, 0),
    event_id: String(orderId),
  })
}

export const trackPromoApplied = (
  promoCode: string,
  discountAmount: number,
  discountType?: string,
  items?: TrackingItem[],
) => {
  const params: Record<string, any> = {
    coupon: promoCode,
    discount: discountAmount,
    currency: trackingCurrency,
  }
  if (discountType) params.discount_type = discountType
  if (items) params.items = mapItems(items)
  fireGA4Event('select_promotion', params)
  // Also push a custom event for GTM triggers
  pushDataLayer('promo_applied', { coupon: promoCode, discount: discountAmount, discount_type: discountType || '' })
}

export const trackCheckoutAbandonment = (
  step: string,
  total: number,
  items?: TrackingItem[],
) => {
  const params: Record<string, any> = {
    currency: trackingCurrency,
    value: total,
    checkout_step: step,
    items: mapItems(items || []),
  }
  fireGA4Event('checkout_abandonment', params)
  pushDataLayer('checkout_abandonment', params)
}
